import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, MessageCircle, Phone } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingCTA from "@/components/site/FloatingCTA";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BLOG_POSTS } from "@/data/blog";
import { CONTACT } from "@/data/content";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Custom Clearance Partner`;

    // Meta description
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = post.description;

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `https://customclearancepartner.in/blog/${post.slug}`;

    // BlogPosting + BreadcrumbList + Speakable + FAQPage schema
    const id = "ccp-post-jsonld";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    const url = `https://customclearancepartner.in/blog/${post.slug}`;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id": `${url}#post`,
          headline: post.title,
          description: post.description,
          keywords: post.keyword,
          datePublished: post.dateISO,
          dateModified: post.dateISO,
          inLanguage: post.slug.endsWith("-ja") ? "ja" : "en-IN",
          author: {
            "@type": "Person",
            name: "Jaisy Atharth",
            jobTitle: "Founder, Custom Clearance Partner",
            url: "https://customclearancepartner.in/#founder-note",
          },
          publisher: {
            "@type": "Organization",
            name: "Custom Clearance Partner",
            "@id": "https://customclearancepartner.in/#org",
          },
          mainEntityOfPage: url,
          articleSection: post.tag,
          wordCount: post.sections.reduce(
            (acc, s) => acc + s.body.join(" ").split(/\s+/).length,
            0,
          ),
          speakable: {
            "@type": "SpeakableSpecification",
            xpath: [
              "//*[@id='blog-post-title']",
              "//*[@id='blog-post-tldr']",
            ],
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://customclearancepartner.in/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://customclearancepartner.in/blog" },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: post.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(script);
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => document.getElementById(id)?.remove();
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="grain min-h-screen bg-[#F9FAFB] text-[#101828]" data-testid={`blog-post-${post.slug}`}>
      <Header />
      <main>
        <article className="border-b border-[#E4E7EC] bg-white">
          <div className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-24">
            <nav className="font-mono-accent flex items-center gap-3 text-[#475467]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#D92D20]">
                HOME
              </Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-[#D92D20]" data-testid="breadcrumb-blog">
                BLOG
              </Link>
              <span>/</span>
              <span className="text-[#101828]">{post.tag.toUpperCase()}</span>
            </nav>

            <header className="mt-8">
              <span className="font-mono-accent text-[#D92D20]">{post.tag}</span>
              <h1
                id="blog-post-title"
                data-testid="blog-post-title"
                className="mt-4 font-display text-4xl font-black leading-[0.98] tracking-tight text-[#101828] sm:text-5xl md:text-6xl"
              >
                {post.title}
              </h1>
              <div className="font-mono-accent mt-6 flex flex-wrap items-center gap-4 text-[#475467]">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" strokeWidth={2.5} />
                  {post.dateDisplay}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" strokeWidth={2.5} />
                  {post.readTime}
                </span>
                <span>· BY JAISY ATHARTH · FOUNDER</span>
              </div>
            </header>

            {/* TL;DR box — the direct-answer block AI engines love */}
            <div
              id="blog-post-tldr"
              data-testid="blog-post-tldr"
              className="mt-10 border-2 border-[#101828] bg-[#FDB022] p-5 md:p-6"
            >
              <p className="font-mono-accent text-[#101828]">TL;DR — DIRECT ANSWER</p>
              <p className="mt-2 text-base leading-relaxed text-[#101828]">{post.tldr}</p>
            </div>

            {/* Body sections */}
            <div className="prose-lg mt-12 space-y-10">
              {post.sections.map((s, i) => (
                <section key={i} className="scroll-mt-24">
                  <h2 className="font-display text-2xl font-bold leading-tight tracking-tight text-[#101828] sm:text-3xl">
                    {s.h2}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {s.body.map((para, j) => (
                      <p key={j} className="text-base leading-relaxed text-[#344054]">
                        {para}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {/* Post FAQ */}
            <section className="mt-14 border-t border-[#E4E7EC] pt-10">
              <h2 className="font-display text-2xl font-bold tracking-tight text-[#101828] sm:text-3xl">
                Frequently asked
              </h2>
              <div className="mt-6 border-2 border-[#101828] bg-white">
                <Accordion type="single" collapsible className="w-full">
                  {post.faq.map((f, i) => (
                    <AccordionItem
                      key={i}
                      value={`fq-${i}`}
                      className="border-b border-[#E4E7EC] last:border-b-0"
                      data-testid={`post-faq-${i}`}
                    >
                      <AccordionTrigger className="px-5 py-4 text-left font-display text-base font-bold tracking-tight text-[#101828] hover:no-underline md:text-lg">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-5 text-sm leading-relaxed text-[#475467]">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </section>

            {/* End-of-post CTA */}
            <section className="mt-12 border-2 border-[#101828] bg-[#101828] p-6 text-white md:p-8">
              <p className="font-mono-accent text-[#FDB022]">TALK TO OUR DESK</p>
              <p className="mt-3 font-display text-xl font-bold leading-tight tracking-tight md:text-2xl">
                Have a shipment that fits this scenario? We reply within 15 minutes on WhatsApp — 24/7.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="post-whatsapp-cta"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#25D366] bg-[#25D366] px-5 py-3 text-sm font-semibold text-[#0B111A] hover:bg-[#1FBE5A]"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={2.5} /> WhatsApp our desk
                </a>
                <a
                  href={CONTACT.telHref}
                  data-testid="post-call-cta"
                  className="inline-flex items-center justify-center gap-2 border-2 border-white bg-transparent px-5 py-3 text-sm font-semibold text-white hover:bg-white hover:text-[#101828]"
                >
                  <Phone className="h-4 w-4" strokeWidth={2.5} /> {CONTACT.phoneDisplay}
                </a>
              </div>
            </section>

            <div className="mt-10">
              <Link
                to="/blog"
                data-testid="back-to-blog"
                className="font-mono-accent inline-flex items-center gap-2 text-[#475467] hover:text-[#D92D20]"
              >
                <ArrowLeft className="h-3 w-3" strokeWidth={3} /> BACK TO ALL POSTS
              </Link>
            </div>
          </div>
        </article>

        {/* Related */}
        <section className="border-b border-[#E4E7EC] bg-[#F9FAFB]" aria-label="Related posts">
          <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
            <p className="overline mb-4">§ Related</p>
            <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl">
              Continue reading
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="bento-card group flex h-full flex-col justify-between border-2 border-[#101828] bg-white p-6 hover:border-[#D92D20]"
                >
                  <div>
                    <span className="font-mono-accent text-[#D92D20]">{p.tag}</span>
                    <h3 className="mt-3 font-display text-lg font-bold leading-tight tracking-tight text-[#101828]">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#475467]">
                      {p.description}
                    </p>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-[#E4E7EC] pt-3">
                    <span className="font-mono-accent text-[10px] text-[#475467]">
                      {p.readTime}
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-[#101828]" strokeWidth={2.5} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
